import { useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { DorkGenerationOptions } from "@/types/dork";
import { useLocale } from "@/contexts/LocaleContext";

interface AdvancedOptionsProps {
  options: DorkGenerationOptions;
  onOptionsChange: (options: DorkGenerationOptions) => void;
}

const FILE_TYPES = ["pdf", "docx", "xlsx", "pptx", "txt", "sql", "csv", "json", "xml", "log"];

export function AdvancedOptions({ options, onOptionsChange }: AdvancedOptionsProps) {
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [excludedDomainInput, setExcludedDomainInput] = useState("");

  const SENSITIVITY_LEVELS = [
    { value: "low", label: t("advanced.sensitivity_low"), description: t("advanced.sensitivity_low_desc") },
    { value: "medium", label: t("advanced.sensitivity_medium"), description: t("advanced.sensitivity_medium_desc") },
    { value: "high", label: t("advanced.sensitivity_high"), description: t("advanced.sensitivity_high_desc") },
  ];

  const toggleFileType = (type: string) => {
    const current = options.fileTypes || [];
    const updated = current.includes(type)
      ? current.filter((ft) => ft !== type)
      : [...current, type];
    onOptionsChange({ ...options, fileTypes: updated });
  };

  const addDomain = () => {
    if (domainInput.trim()) {
      const current = options.domains || [];
      if (!current.includes(domainInput.trim())) {
        onOptionsChange({ ...options, domains: [...current, domainInput.trim()] });
      }
      setDomainInput("");
    }
  };

  const removeDomain = (domain: string) => {
    const updated = (options.domains || []).filter((d) => d !== domain);
    onOptionsChange({ ...options, domains: updated });
  };

  const addExcludedDomain = () => {
    if (excludedDomainInput.trim()) {
      const current = options.excludedDomains || [];
      if (!current.includes(excludedDomainInput.trim())) {
        onOptionsChange({ ...options, excludedDomains: [...current, excludedDomainInput.trim()] });
      }
      setExcludedDomainInput("");
    }
  };

  const removeExcludedDomain = (domain: string) => {
    const updated = (options.excludedDomains || []).filter((d) => d !== domain);
    onOptionsChange({ ...options, excludedDomains: updated });
  };

  return (
    <div className="glass-card overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/20 transition-colors"
      >
        <span className="text-sm font-medium text-foreground">{t("advanced.title")}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {isOpen && (
        <div className="p-4 pt-0 space-y-6 animate-fade-in border-t border-border/30">
          {/* File Types */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-3">
              {t("advanced.file_types")}
            </label>
            <div className="flex flex-wrap gap-2">
              {FILE_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleFileType(type)}
                  className={`px-3 py-1.5 text-xs font-mono rounded-lg border transition-all duration-200 ${
                    options.fileTypes?.includes(type)
                      ? "bg-primary/20 border-primary/50 text-primary"
                      : "bg-muted/30 border-border/30 text-muted-foreground hover:border-border"
                  }`}
                >
                  .{type}
                </button>
              ))}
            </div>
          </div>

          {/* Target Domains */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-3">
              {t("advanced.target_domains")}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDomain()}
                placeholder="example.com"
                className="flex-1 terminal-input text-sm"
              />
              <button
                onClick={addDomain}
                className="px-4 py-2 bg-muted/50 hover:bg-muted text-foreground text-sm rounded-lg border border-border/30 transition-colors"
              >
                {t("advanced.add")}
              </button>
            </div>
            {options.domains && options.domains.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {options.domains.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono bg-primary/10 text-primary rounded-lg border border-primary/30"
                  >
                    {domain}
                    <button onClick={() => removeDomain(domain)} className="hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Excluded Domains */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-3">
              {t("advanced.excluded_domains")}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={excludedDomainInput}
                onChange={(e) => setExcludedDomainInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addExcludedDomain()}
                placeholder="spam.com"
                className="flex-1 terminal-input text-sm"
              />
              <button
                onClick={addExcludedDomain}
                className="px-4 py-2 bg-muted/50 hover:bg-muted text-foreground text-sm rounded-lg border border-border/30 transition-colors"
              >
                {t("advanced.add")}
              </button>
            </div>
            {options.excludedDomains && options.excludedDomains.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {options.excludedDomains.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono bg-destructive/10 text-destructive rounded-lg border border-destructive/30"
                  >
                    {domain}
                    <button onClick={() => removeExcludedDomain(domain)} className="hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Sensitivity Level */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-3">
              {t("advanced.sensitivity")}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {SENSITIVITY_LEVELS.map((level) => (
                <button
                  key={level.value}
                  onClick={() =>
                    onOptionsChange({
                      ...options,
                      sensitivityLevel: level.value as "low" | "medium" | "high",
                    })
                  }
                  className={`p-3 rounded-lg border text-left transition-all duration-200 ${
                    options.sensitivityLevel === level.value
                      ? "bg-primary/10 border-primary/50"
                      : "bg-muted/20 border-border/30 hover:border-border"
                  }`}
                >
                  <div
                    className={`text-sm font-medium mb-0.5 ${
                      options.sensitivityLevel === level.value
                        ? "text-primary"
                        : "text-foreground"
                    }`}
                  >
                    {level.label}
                  </div>
                  <div className="text-xs text-muted-foreground">{level.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
